import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCopy,
  faEnvelope,
  faUserPlus,
  faUpRightFromSquare,
  faAngleLeft,
  faAngleRight,
  faAnglesLeft,
  faAnglesRight,
} from '@fortawesome/free-solid-svg-icons';
import { User } from "../types"
import { API_BASE_URL } from '../config/api';
import SentryDropdown, { sentryExactQuery } from './SentryDropdown';
import { TESTER_OPTIONS, resolveTester, testerBadgeClass, testerLabel } from '../utils/tester';
import { DEFAULT_LOGGING_MODE, loggingModeShortLabel, loggingModeBadgeStyle } from '../utils/loggingMode';
import { roleBadgeClass } from '../utils/roleBadge';
import AppDevicesTable from './AppDevicesTable';

interface UserManagementProps {
  user: User;
}

interface NewUserData {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  role: string;
}

const USER_ROLES: { value: User['role']; label: string }[] = [
  { value: 'customer', label: 'Customer' },
  { value: 'support', label: 'Support' },
  { value: 'technician', label: 'Technician' },
  { value: 'factory', label: 'Factory' },
  { value: 'developer', label: 'Developer' },
  { value: 'admin', label: 'Admin' },
];

const UserManagement: React.FC<UserManagementProps> = ({ user }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [message, setMessage] = useState<string>("");
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [newUser, setNewUser] = useState<NewUserData>({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    role: "customer"
  });
  const [createLoading, setCreateLoading] = useState<boolean>(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const canCreateUsers = ['admin', 'developer'].includes(user.role);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [limit] = useState<number>(10);

  const [searchId, setSearchId] = useState<string>(searchParams.get('id') || '');
  const [searchEmail, setSearchEmail] = useState<string>(searchParams.get('email') || '');
  const [searchFirstName, setSearchFirstName] = useState<string>(searchParams.get('firstName') || '');
  const [searchLastName, setSearchLastName] = useState<string>(searchParams.get('lastName') || '');
  const [searchRole, setSearchRole] = useState<string>(searchParams.get('role') || '');
  const [searchTester, setSearchTester] = useState<string>(searchParams.get('tester') || '');
  const [isSearching, setIsSearching] = useState<boolean>(
    Boolean(
      searchParams.get('id') ||
      searchParams.get('email') ||
      searchParams.get('firstName') ||
      searchParams.get('lastName') ||
      searchParams.get('role') ||
      searchParams.get('tester')
    )
  );

  const fetchUsers = useCallback(async (page: number) => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, limit };

      const id = searchParams.get('id');
      const email = searchParams.get('email');
      const firstName = searchParams.get('firstName');
      const lastName = searchParams.get('lastName');
      const role = searchParams.get('role');
      const tester = searchParams.get('tester');

      if (id) params.id = id;
      if (email) params.email = email;
      if (firstName) params.firstName = firstName;
      if (lastName) params.lastName = lastName;
      if (role) params.role = role;
      if (tester) params.tester = tester;

      const response = await axios.get(`${API_BASE_URL}/users`, {
        withCredentials: true,
        params,
      });

      setUsers(response.data.users);
      setTotalPages(response.data.totalPages);
      setCurrentPage(page);
      setTotalUsers(response.data.totalUsers);
      setIsSearching(Boolean(response.data.hasFilters));
      setError(response.data.totalUsers === 0 && response.data.hasFilters
        ? 'No users found matching the current filters'
        : null
      );
    } catch (err: any) {
      console.error("Error fetching users:", err.response?.data || err.message);
      setError(err.response?.data?.message || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, [limit, searchParams]);

  const copyToClipboard = (id: string) => {
    navigator.clipboard.writeText(id)
      .then(() => {
        setMessage(`User ID ${id} copied to clipboard`);
        setTimeout(() => setMessage(''), 2000);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        setMessage('Failed to copy ID');
      });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewUser({
      ...newUser,
      [name]: value
    });
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError(null);

    try {
      await axios.post(
        `${API_BASE_URL}/users`,
        newUser,
        { withCredentials: true }
      );

      setNewUser({
        email: "",
        firstName: "",
        lastName: "",
        password: "",
        role: "customer"
      });

      setMessage(`User ${newUser.email} created successfully.`);
      setShowCreateModal(false);
      fetchUsers(1);
    } catch (err: any) {
      console.error("Error creating user:", err.response?.data || err.message);
      setCreateError(err.response?.data?.message || "Failed to create user");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    const nextParams = new URLSearchParams();
    if (searchId.trim()) nextParams.set('id', searchId.trim());
    if (searchEmail.trim()) nextParams.set('email', searchEmail.trim());
    if (searchFirstName.trim()) nextParams.set('firstName', searchFirstName.trim());
    if (searchLastName.trim()) nextParams.set('lastName', searchLastName.trim());
    if (searchRole) nextParams.set('role', searchRole);
    if (searchTester) nextParams.set('tester', searchTester);

    setSearchParams(nextParams);
  };

  const handleClearSearch = () => {
    setSearchId('');
    setSearchEmail('');
    setSearchFirstName('');
    setSearchLastName('');
    setSearchRole('');
    setSearchTester('');
    setSearchParams({});
  };

  useEffect(() => {
    setSearchId(searchParams.get('id') || '');
    setSearchEmail(searchParams.get('email') || '');
    setSearchFirstName(searchParams.get('firstName') || '');
    setSearchLastName(searchParams.get('lastName') || '');
    setSearchRole(searchParams.get('role') || '');
    setSearchTester(searchParams.get('tester') || '');
    fetchUsers(1);
  }, [searchParams, fetchUsers]);

  useEffect(() => {
    if (showCreateModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showCreateModal]);

  return (
    <div className="container-fluid mt-3">
      <div className="row mb-3">
        <div className="col responsive-toolbar d-flex justify-content-between align-items-center">
          <h3>Manage Users</h3>
          {canCreateUsers && (
            <button
              className="btn btn-outline-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <FontAwesomeIcon icon={faUserPlus} className="me-2" /> Add New User
            </button>
          )}
        </div>
      </div>

      <div className="responsive-filter-bar d-flex flex-nowrap align-items-center gap-2 mb-3">
        <form onSubmit={handleSearch} className="d-flex flex-nowrap align-items-center gap-2 flex-grow-1 min-w-0">
          <input
            type="text"
            className="form-control form-control-sm flex-shrink-0"
            placeholder="ID"
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
            style={{ width: '120px' }}
          />
          <input
            type="text"
            className="form-control form-control-sm flex-shrink-0"
            placeholder="Email"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            style={{ width: '160px' }}
          />
          <input
            type="text"
            className="form-control form-control-sm flex-shrink-0"
            placeholder="First Name"
            value={searchFirstName}
            onChange={(e) => setSearchFirstName(e.target.value)}
            style={{ width: '120px' }}
          />
          <input
            type="text"
            className="form-control form-control-sm flex-shrink-0"
            placeholder="Last Name"
            value={searchLastName}
            onChange={(e) => setSearchLastName(e.target.value)}
            style={{ width: '120px' }}
          />
          <div className="input-group input-group-sm flex-shrink-0" style={{ width: 'auto' }}>
            <span className="input-group-text">Role</span>
            <select
              className="form-select"
              value={searchRole}
              onChange={(e) => setSearchRole(e.target.value)}
              aria-label="Filter by role"
              style={{ width: '120px' }}
            >
              <option value="">All</option>
              {USER_ROLES.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div className="input-group input-group-sm flex-shrink-0" style={{ width: 'auto' }}>
            <span className="input-group-text">Testing</span>
            <select
              className="form-select"
              value={searchTester}
              onChange={(e) => setSearchTester(e.target.value)}
              aria-label="Filter by testing"
              style={{ width: '120px' }}
            >
              <option value="">All</option>
              {TESTER_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <button type="submit" className="btn btn-primary btn-sm flex-shrink-0">Search</button>
          {isSearching && (
            <button type="button" className="btn btn-secondary btn-sm flex-shrink-0" onClick={handleClearSearch}>
              Clear
            </button>
          )}
        </form>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '280px' }}
        >
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <table className="table table-striped">
            <thead>
              <tr>
                <th style={{ width: '150px' }}>ID</th>
                <th className="ps-4">Email</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Role</th>
                <th>Testing</th>
                <th>Logging</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? (
                users.map((u) => (
                  <tr key={u.email}>
                    <td style={{ maxWidth: '150px' }}>
                      <div className="d-flex align-items-center min-w-0">
                        <button
                          type="button"
                          className="btn btn-link p-0 me-2 text-secondary flex-shrink-0"
                          onClick={() => copyToClipboard(u._id)}
                          title="Copy User ID"
                          aria-label="Copy User ID"
                        >
                          <FontAwesomeIcon icon={faCopy} />
                        </button>
                        <span
                          className="text-truncate min-w-0"
                          title={u._id}
                        >
                          {u._id}
                        </span>
                      </div>
                    </td>
                    <td className="ps-4">
                      <span className="d-flex align-items-center min-w-0">
                        <FontAwesomeIcon icon={faEnvelope} className="text-secondary me-2 flex-shrink-0" size="sm" />
                        <span className="text-truncate" title={u.email}>{u.email}</span>
                      </span>
                    </td>
                    <td>{u.firstName}</td>
                    <td>{u.lastName}</td>
                    <td>
                      <span className={`badge ${roleBadgeClass(u.role)}`}>
                        {u.role}
                      </span>
                    </td>
                    <td>
                      {resolveTester(u) === 'none' ? (
                        <span className="text-muted">—</span>
                      ) : (
                        <span className={`badge text-lowercase ${testerBadgeClass(resolveTester(u))}`}>
                          {testerLabel(resolveTester(u))}
                        </span>
                      )}
                    </td>
                    <td>
                      <span
                        className="badge text-lowercase"
                        style={loggingModeBadgeStyle(u.loggingMode ?? DEFAULT_LOGGING_MODE)}
                      >
                        {loggingModeShortLabel(u.loggingMode ?? DEFAULT_LOGGING_MODE)}
                      </span>
                    </td>
                    <td className="text-nowrap text-muted small">
                      {u.lastLogin
                        ? new Date(u.lastLogin).toLocaleString()
                        : '—'}
                    </td>
                    <td className="text-nowrap">
                      <SentryDropdown
                        issuesQuery={sentryExactQuery('user.id', u._id)}
                        logsFields={['timestamp', 'message', 'user.id']}
                      />
                      <Link
                        className="btn btn-outline-primary btn-sm"
                        to={`/users/${u._id}`}
                        title="View user details"
                      >
                        View <FontAwesomeIcon icon={faUpRightFromSquare} className="ms-1" />
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="text-center">No users found</td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 0 && (
            <div className="responsive-pagination d-flex justify-content-between align-items-center mt-2">
              <div className="fw-bold pt-1">
                {totalUsers.toLocaleString()} total users
              </div>
              <nav aria-label="User pagination">
                <ul className="pagination mb-0">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => fetchUsers(1)}
                      disabled={currentPage === 1}
                      aria-label="First page"
                    >
                      <FontAwesomeIcon icon={faAnglesLeft} />
                    </button>
                  </li>
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => fetchUsers(currentPage - 1)}
                      disabled={currentPage === 1}
                      aria-label="Previous page"
                    >
                      <FontAwesomeIcon icon={faAngleLeft} />
                    </button>
                  </li>

                  <li className="page-item disabled">
                    <span className="page-link">
                      Page {currentPage} of {totalPages}
                    </span>
                  </li>

                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => fetchUsers(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      aria-label="Next page"
                    >
                      <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                  </li>
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => fetchUsers(totalPages)}
                      disabled={currentPage === totalPages}
                      aria-label="Last page"
                    >
                      <FontAwesomeIcon icon={faAnglesRight} />
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      )}

      {message && <p className="mt-3 text-success">{message}</p>}

      {canCreateUsers && <AppDevicesTable />}

      {showCreateModal && canCreateUsers && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Create New User</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowCreateModal(false)}
                    aria-label="Close"
                  ></button>
                </div>
                <form onSubmit={handleCreateUser}>
                  <div className="modal-body">
                    {createError && (
                      <div className="alert alert-danger">{createError}</div>
                    )}

                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        value={newUser.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="firstName" className="form-label">First Name</label>
                      <input
                        type="text"
                        className="form-control"
                        id="firstName"
                        name="firstName"
                        value={newUser.firstName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="lastName" className="form-label">Last Name</label>
                      <input
                        type="text"
                        className="form-control"
                        id="lastName"
                        name="lastName"
                        value={newUser.lastName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="password" className="form-label">Password</label>
                      <input
                        type="password"
                        className="form-control"
                        id="password"
                        name="password"
                        value={newUser.password}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="role" className="form-label">Role</label>
                      <select
                        className="form-select"
                        id="role"
                        name="role"
                        value={newUser.role}
                        onChange={handleInputChange}
                        required
                      >
                        {USER_ROLES.map(({ value, label }) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowCreateModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={createLoading}
                    >
                      {createLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Creating...
                        </>
                      ) : 'Create User'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserManagement;
